import { FC, FormEvent } from 'react'
import { useTranslation } from 'react-i18next'

import { Search } from '@mui/icons-material'
import { Box, Button, MenuItem, Stack, TextField, Typography } from '@mui/material'

import appStoreService from '../../../../../services/AppStoreService'
import { AdDataSearchForm as AdDataSearchFormValues } from '../../types/AdData'
import { artGenreOptions, artStyleOptions, isAdDataSearchReady, isAppIdInvalid } from '../helpers/adDataHelpers'

type AdDataSearchFormProps = {
  value: AdDataSearchFormValues
  subgenres: { id: string; name: string }[]
  isSearching?: boolean
  onChange: (value: AdDataSearchFormValues) => void
  onSearch: () => void
}

export const AdDataSearchForm: FC<AdDataSearchFormProps> = ({ value, subgenres, isSearching, onChange, onSearch }) => {
  const { t } = useTranslation()
  const hasAppId = value.appId.trim() !== ''
  const appIdInvalid = isAppIdInvalid(value.appId)
  const filtersDisabled = hasAppId || isSearching
  const set = (field: keyof AdDataSearchFormValues) => (event: { target: { value: string } }) => onChange({ ...value, [field]: event.target.value })

  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (isAdDataSearchReady(value) && !isSearching) onSearch()
  }

  const none = <MenuItem value="">{t('internal-service-management:ad_data_any')}</MenuItem>

  return (
    <Box component="form" onSubmit={submit} noValidate>
      <Box sx={{ display: 'grid', gap: 2, alignItems: 'start', gridTemplateColumns: { xs: '1fr', md: 'minmax(160px, 1fr) auto repeat(4, minmax(0, 1fr))' } }}>
        <TextField
          size="small"
          label={t('internal-service-management:ad_data_app_id')}
          value={value.appId}
          onChange={set('appId')}
          disabled={isSearching}
          error={appIdInvalid}
          helperText={
            appIdInvalid
              ? t('internal-service-management:ad_data_app_id_invalid')
              : hasAppId
              ? t('internal-service-management:ad_data_app_id_ignores_filters')
              : ' '
          }
          inputProps={{ inputMode: 'numeric' }}
        />
        <Typography variant="body2" color="text.secondary" fontWeight="bold" sx={{ pt: 1 }}>
          {t('internal-service-management:ad_data_or')}
        </Typography>
        <TextField
          select
          size="small"
          label={t('internal-service-management:ad_data_subgenre')}
          value={value.conventionalSubgenreId}
          onChange={set('conventionalSubgenreId')}
          disabled={filtersDisabled}
        >
          {none}
          {subgenres.map((subgenre) => (
            <MenuItem key={subgenre.id} value={subgenre.id}>
              {subgenre.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label={t('internal-service-management:ad_data_art_style')}
          value={value.artStyleId}
          onChange={set('artStyleId')}
          disabled={filtersDisabled}
        >
          {none}
          {artStyleOptions.map((style) => (
            <MenuItem key={style} value={style}>
              {style}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label={t('internal-service-management:ad_data_art_genre')}
          value={value.artGenreId}
          onChange={set('artGenreId')}
          disabled={filtersDisabled}
        >
          {none}
          {artGenreOptions.map((genre) => (
            <MenuItem key={genre} value={genre}>
              {genre}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label={t('internal-service-management:ad_data_app_store_genre')}
          value={value.appStoreGenreId}
          onChange={set('appStoreGenreId')}
          disabled={filtersDisabled}
        >
          <MenuItem value="">{t('internal-service-management:ad_data_app_store_genre_games')}</MenuItem>
          {Object.entries(appStoreService.appStoreGenreMap).map(([id, name]) => (
            <MenuItem key={id} value={id}>
              {name}
            </MenuItem>
          ))}
        </TextField>
      </Box>
      <Stack direction="row" justifyContent="center" mt={1}>
        <Button type="submit" variant="contained" startIcon={<Search />} disabled={!isAdDataSearchReady(value) || isSearching}>
          {t('internal-service-management:ad_data_search')}
        </Button>
      </Stack>
    </Box>
  )
}
